import type { FccOperatorRoleType } from '@backoffice-frontend/graphql'
import type { HandlingOperatorVehicleFragment } from './getHandlingOperatorForRole.hook'

type HandledByEntry = HandlingOperatorVehicleFragment['fccHandledBy'][number]

/**
 * The operator holding a vehicle, normalised across the role-scoped
 * fccHandledBy entries and the legacy roleless field.
 */
export type HandlingOperator = Pick<
  HandledByEntry,
  'backofficeUserId' | 'backofficeUser'
>

/**
 * The operator handling this vehicle under the active role, or under the legacy
 * roleless field when no role is in scope (the AD fleet map).
 *
 * When a role *is* in scope, a vehicle held only rolelessly reads as unhandled:
 * we deliberately do not fall back, so role-scoped surfaces never attribute a
 * roleless handover to their own role.
 *
 * The vehicle is `Partial` because consumers may gate the fragment behind an
 * `@include` directive, in which case none of the fields are present.
 *
 * Delete the roleless branch together with libs/ad-fleet-map — it is the only
 * surface without a role in scope.
 */
export const getHandlingOperatorForRole = (
  vehicle: Partial<HandlingOperatorVehicleFragment>,
  role: FccOperatorRoleType | undefined,
): HandlingOperator | undefined => {
  if (role) {
    return (vehicle.fccHandledBy ?? []).find(entry => entry.role === role)
  }

  return vehicle.fccHandledByOperator
    ? {
        backofficeUserId: vehicle.fccHandledByOperator.id,
        // The roleless field exposes names only through this alias. The alias is
        // absent, not null, when the fragment is gated off by an @include.
        backofficeUser: vehicle.fccHandledByOperatorDetails ?? null,
      }
    : undefined
}
